import { useSessionTokenGetter } from '@develio/auth/client';
import { useCallback, useEffect, useState } from 'react';
import {
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { PipelineStatusBanner } from '../components/PipelineStatusBanner';
import { Surface } from '../components/Surface';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

type Dimensions = {
  workforceAlignment: number;
  wageCompetitiveness: number;
  industryPresence: number;
  nationalGrowth: number;
  capitalIntensityFit: number;
};

type IndustryScore = {
  naicsCode: string;
  label: string;
  composite: number;
  dimensions: Dimensions;
  data: {
    localEmployment: number | null;
    impliedLocationQuotient: number | null;
    qcewGrowth3yr: number | null;
    avgWeeklyWage: number | null;
    localMedianWage: number | null;
    nationalMedianWage: number | null;
    beaGdpCagr: number | null;
    beaCapitalIntensity: number | null;
    releaseYear: number | null;
  };
};

type Override = {
  id: string;
  naicsCode: string;
  overrideScore: string | null;
  notes: string | null;
};

type ProfileData = {
  scores: IndustryScore[];
  overrides: Override[];
  geography: { countyFips: string; areaName: string } | null;
  defaultWeights: Record<string, number>;
  reason: string;
};

const DIM_LABELS: Record<keyof Dimensions, string> = {
  workforceAlignment: 'Workforce Alignment',
  wageCompetitiveness: 'Wage Competitiveness',
  industryPresence: 'Industry Presence',
  nationalGrowth: 'National Growth',
  capitalIntensityFit: 'Capital Intensity Fit',
};

const DIM_TIPS: Record<keyof Dimensions, string> = {
  workforceAlignment: 'Local occupational supply vs. industry demand (OES ÷ QCEW)',
  wageCompetitiveness: 'Local wages below national = cost advantage (OES)',
  industryPresence: 'Implied location quotient + 3-yr employment growth trend (QCEW)',
  nationalGrowth: 'BEA GDP CAGR — higher growth = more tailwinds',
  capitalIntensityFit: 'Lower fixed-assets/GDP = easier to attract (BEA)',
};

function scoreColor(score: number): string {
  if (score >= 70) return '#0f766e'; // teal — strong
  if (score >= 55) return '#0891b2'; // blue — good
  if (score >= 40) return '#f59e0b'; // amber — moderate
  return '#ef4444'; // red — weak
}

function RankBadge({ rank }: { rank: number }) {
  return (
    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-600">
      {rank}
    </span>
  );
}

function ScoreBar({ value, color }: { value: number; color: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 flex-1 rounded-full bg-slate-100">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${value}%`, backgroundColor: color }}
        />
      </div>
      <span className="w-8 text-right text-xs tabular-nums text-slate-500">{value}</span>
    </div>
  );
}

type OverrideFormState = { score: string; notes: string };

function OverridePanel({
  naicsCode,
  label,
  current,
  onSave,
  onDelete,
  saving,
}: {
  naicsCode: string;
  label: string;
  current: Override | undefined;
  onSave: (naicsCode: string, score: string, notes: string) => Promise<void>;
  onDelete: (naicsCode: string) => Promise<void>;
  saving: boolean;
}) {
  const [form, setForm] = useState<OverrideFormState>({
    score: current?.overrideScore ?? '',
    notes: current?.notes ?? '',
  });

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
        {label} — Director Override
      </p>
      <div className="flex gap-3">
        <div className="w-28">
          <label className="text-[10px] text-slate-400">Score override (0–100)</label>
          <input
            type="number"
            min={0}
            max={100}
            className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-teal-500 focus:outline-none"
            placeholder="Leave blank = model"
            value={form.score}
            onChange={(e) => setForm((f) => ({ ...f, score: e.target.value }))}
          />
        </div>
        <div className="flex-1">
          <label className="text-[10px] text-slate-400">Notes (reason for override)</label>
          <input
            type="text"
            className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-teal-500 focus:outline-none"
            placeholder="e.g. Strategic priority per city council"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={saving}
          className="rounded bg-teal-600 px-3 py-1 text-xs font-medium text-white hover:bg-teal-700 disabled:opacity-50"
          onClick={() => void onSave(naicsCode, form.score, form.notes)}
        >
          {saving ? 'Saving…' : 'Save override'}
        </button>
        {current && (
          <button
            type="button"
            disabled={saving}
            className="rounded border border-red-200 bg-red-50 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
            onClick={() => void onDelete(naicsCode)}
          >
            Remove override
          </button>
        )}
      </div>
    </div>
  );
}

const DEFAULT_WEIGHTS = {
  workforceAlignment: 25,
  wageCompetitiveness: 20,
  industryPresence: 25,
  nationalGrowth: 20,
  capitalIntensityFit: 10,
};
type WeightKey = keyof typeof DEFAULT_WEIGHTS;

function recomputeComposite(dimensions: Dimensions, weights: typeof DEFAULT_WEIGHTS): number {
  const total = Object.values(weights).reduce((s, v) => s + v, 0);
  if (total === 0) return 0;
  const weighted = (Object.keys(weights) as WeightKey[]).reduce(
    (s, k) => s + dimensions[k] * weights[k],
    0,
  );
  return Math.round((weighted / total) * 10) / 10;
}

export function IndustryTargetRoute() {
  const getToken = useSessionTokenGetter();
  const [data, setData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [savingOverride, setSavingOverride] = useState<string | null>(null);
  const [weights, setWeights] = useState({ ...DEFAULT_WEIGHTS });
  const [showWeights, setShowWeights] = useState(false);
  const [downloading, setDownloading] = useState(false);

  async function downloadPdf() {
    setDownloading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/industry-target/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `industry-targets-${data?.geography?.countyFips ?? 'export'}-${new Date().getFullYear()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }

  const fetchScores = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/industry-target`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData((await res.json()) as ProfileData);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    void fetchScores();
  }, [fetchScores]);

  async function saveOverride(naicsCode: string, score: string, notes: string) {
    setSavingOverride(naicsCode);
    try {
      const token = await getToken();
      await fetch(`${API}/api/v1/industry-target/overrides/${naicsCode}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          overrideScore: score !== '' ? Number(score) : null,
          notes: notes || null,
        }),
      });
      await fetchScores();
      setExpandedRow(null);
    } finally {
      setSavingOverride(null);
    }
  }

  async function deleteOverride(naicsCode: string) {
    setSavingOverride(naicsCode);
    try {
      const token = await getToken();
      await fetch(`${API}/api/v1/industry-target/overrides/${naicsCode}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchScores();
      setExpandedRow(null);
    } finally {
      setSavingOverride(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <p className="text-sm text-slate-400">Computing industry scores…</p>
      </div>
    );
  }

  if (!data?.geography) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-8">
        <h1 className="mb-2 text-2xl font-bold text-slate-900">Industry Target Scoring</h1>
        <Surface className="p-6">
          <p className="text-sm text-amber-700">
            No geography configured. Add a County FIPS code to your default Jurisdiction Profile in{' '}
            <a href="/settings/jurisdiction" className="underline">
              Settings → Jurisdiction
            </a>
            .
          </p>
        </Surface>
      </div>
    );
  }

  const { scores, overrides, geography } = data;
  const overrideMap = new Map(overrides.map((o) => [o.naicsCode, o]));

  // Effective score = override if set, else composite recomputed with current weights
  const effectiveScores = scores
    .map((s) => {
      const ov = overrideMap.get(s.naicsCode);
      const modelComposite = recomputeComposite(s.dimensions, weights);
      const effectiveComposite =
        ov?.overrideScore != null ? Number(ov.overrideScore) : modelComposite;
      return { ...s, modelComposite, effectiveComposite, hasOverride: ov != null };
    })
    .sort((a, b) => b.effectiveComposite - a.effectiveComposite);

  // Quadrant chart: x = nationalGrowth, y = (workforceAlignment + industryPresence) / 2
  const quadrantData = effectiveScores.map((s) => ({
    name: s.label,
    naicsCode: s.naicsCode,
    x: s.dimensions.nationalGrowth,
    y: Math.round((s.dimensions.workforceAlignment + s.dimensions.industryPresence) / 2),
    z: Math.max(8, Math.min(40, (s.data.localEmployment ?? 0) / 500 + 8)),
    composite: s.effectiveComposite,
  }));

  const hasAnyData = scores.some((s) => s.data.localEmployment != null);

  return (
    <div className="mx-auto max-w-5xl px-6 py-8 space-y-8">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Industry Target Scoring</h1>
          <p className="mt-1 text-sm text-slate-500">
            {geography.areaName} · Ranked composite across workforce, wage, presence, growth, and
            capital fit
          </p>
        </div>
        <button
          type="button"
          onClick={() => void downloadPdf()}
          disabled={downloading}
          className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
        >
          {downloading ? 'Generating…' : 'Download PDF'}
        </button>
      </div>

      <PipelineStatusBanner />

      {!hasAnyData && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          No local data loaded yet. Run the QCEW and OES pipelines for your county to populate
          scores. Scores currently show neutral (50) for all dimensions with missing data.
        </div>
      )}

      {/* Quadrant chart */}
      <Surface className="p-5">
        <h2 className="mb-1 text-sm font-semibold text-slate-700">Growth × Local Fit Quadrant</h2>
        <p className="mb-4 text-xs text-slate-400">
          X = national growth score · Y = local fit (workforce + presence average) · bubble size =
          local employment
        </p>
        <ResponsiveContainer width="100%" height={300}>
          <ScatterChart margin={{ top: 8, right: 24, bottom: 24, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              dataKey="x"
              domain={[0, 100]}
              label={{
                value: 'National Growth →',
                position: 'insideBottom',
                offset: -12,
                fontSize: 11,
              }}
              tick={{ fontSize: 10 }}
            />
            <YAxis
              type="number"
              dataKey="y"
              domain={[0, 100]}
              label={{ value: 'Local Fit →', angle: -90, position: 'insideLeft', fontSize: 11 }}
              tick={{ fontSize: 10 }}
            />
            <ZAxis type="number" dataKey="z" range={[30, 400]} />
            <Tooltip
              cursor={{ strokeDasharray: '3 3' }}
              content={({ payload }) => {
                const d = payload?.[0]?.payload as (typeof quadrantData)[0] | undefined;
                if (!d) return null;
                return (
                  <div className="rounded border border-slate-200 bg-white p-2 shadow text-xs">
                    <p className="font-semibold text-slate-800">{d.name}</p>
                    <p className="text-slate-500">
                      Growth: {d.x} · Fit: {d.y} · Score: {d.composite.toFixed(1)}
                    </p>
                  </div>
                );
              }}
            />
            <Scatter data={quadrantData} fill="#0f766e" fillOpacity={0.75}>
              {quadrantData.map((d, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: Recharts Cell has no stable id
                <Cell key={i} fill={scoreColor(d.composite)} fillOpacity={0.8} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
        {/* Quadrant labels */}
        <div className="mt-2 grid grid-cols-2 gap-2 text-[10px] text-slate-400 text-center">
          <div className="rounded bg-slate-50 p-1">High fit, low growth → Defend &amp; Retain</div>
          <div className="rounded bg-teal-50 p-1 text-teal-700 font-medium">
            High fit, high growth → Primary Targets ★
          </div>
          <div className="rounded bg-slate-50 p-1">Low fit, low growth → Avoid or Transform</div>
          <div className="rounded bg-blue-50 p-1 text-blue-700">
            Low fit, high growth → Build Capacity First
          </div>
        </div>
      </Surface>

      {/* Scoring Weights panel */}
      <Surface className="p-5">
        <button
          type="button"
          className="flex w-full items-center justify-between text-sm font-semibold text-slate-700"
          onClick={() => setShowWeights((v) => !v)}
        >
          <span>Scoring Weights</span>
          <span className="text-xs font-normal text-slate-400">
            {showWeights
              ? 'Collapse'
              : `Workforce ${weights.workforceAlignment}% · Wage ${weights.wageCompetitiveness}% · Presence ${weights.industryPresence}% · Growth ${weights.nationalGrowth}% · Capital ${weights.capitalIntensityFit}%`}
          </span>
        </button>
        {showWeights && (
          <div className="mt-4 space-y-3">
            <p className="text-xs text-slate-400">
              Adjust dimension weights. The composite score recomputes live — no API call needed.{' '}
              Weights do not need to sum to 100.
            </p>
            {(Object.keys(DEFAULT_WEIGHTS) as WeightKey[]).map((key) => (
              <div key={key} className="flex items-center gap-3">
                <span className="w-44 text-xs text-slate-600">{DIM_LABELS[key]}</span>
                <input
                  type="range"
                  min={0}
                  max={50}
                  step={5}
                  value={weights[key]}
                  onChange={(e) =>
                    setWeights((prev) => ({ ...prev, [key]: Number(e.target.value) }))
                  }
                  className="flex-1 accent-teal-600"
                />
                <span className="w-8 text-right text-xs tabular-nums font-medium text-slate-700">
                  {weights[key]}%
                </span>
              </div>
            ))}
            <button
              type="button"
              className="mt-1 text-xs text-slate-400 hover:text-slate-600 underline"
              onClick={() => setWeights({ ...DEFAULT_WEIGHTS })}
            >
              Reset to defaults
            </button>
          </div>
        )}
      </Surface>

      {/* Ranked table */}
      <Surface className="p-5">
        <h2 className="mb-4 text-sm font-semibold text-slate-700">Ranked Sectors</h2>
        <div className="space-y-1">
          {effectiveScores.map((s, i) => {
            const color = scoreColor(s.effectiveComposite);
            const ov = overrideMap.get(s.naicsCode);
            const isExpanded = expandedRow === s.naicsCode;

            return (
              <div
                key={s.naicsCode}
                className="rounded-lg border border-slate-100 hover:border-slate-200"
              >
                {/* Row header */}
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-3 text-left"
                  onClick={() => setExpandedRow(isExpanded ? null : s.naicsCode)}
                >
                  <RankBadge rank={i + 1} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-800 truncate">{s.label}</span>
                      {s.hasOverride && (
                        <span className="inline-flex rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700 border border-indigo-200">
                          Override
                        </span>
                      )}
                    </div>
                    <div className="mt-1 w-48">
                      <ScoreBar value={Math.round(s.effectiveComposite)} color={color} />
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="text-lg font-bold tabular-nums" style={{ color }}>
                      {s.effectiveComposite.toFixed(1)}
                    </span>
                    {ov?.overrideScore != null && (
                      <span className="ml-1 text-xs text-slate-400">
                        (model: {s.modelComposite.toFixed(1)})
                      </span>
                    )}
                  </div>
                  <svg
                    className={`ml-2 h-4 w-4 text-slate-300 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                    viewBox="0 0 12 12"
                    fill="currentColor"
                  >
                    <path d="M4 2l4 4-4 4V2z" />
                  </svg>
                </button>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="border-t border-slate-100 px-4 pb-4 pt-3 space-y-4">
                    {/* Dimension breakdown */}
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      {(Object.keys(DIM_LABELS) as Array<keyof Dimensions>).map((dim) => (
                        <div key={dim} className="rounded bg-slate-50 p-3">
                          <p
                            className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1"
                            title={DIM_TIPS[dim]}
                          >
                            {DIM_LABELS[dim]}
                          </p>
                          <ScoreBar
                            value={s.dimensions[dim]}
                            color={scoreColor(s.dimensions[dim])}
                          />
                        </div>
                      ))}
                    </div>

                    {/* Raw data callouts */}
                    <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
                      {[
                        {
                          label: 'Local Jobs (QCEW)',
                          value: s.data.localEmployment?.toLocaleString() ?? '—',
                        },
                        {
                          label: 'Implied LQ',
                          value: s.data.impliedLocationQuotient?.toFixed(2) ?? '—',
                        },
                        {
                          label: '3-yr Employment Growth',
                          value:
                            s.data.qcewGrowth3yr != null
                              ? `${(s.data.qcewGrowth3yr * 100).toFixed(1)}%`
                              : '—',
                        },
                        {
                          label: 'Local Median Wage',
                          value: s.data.localMedianWage
                            ? `$${s.data.localMedianWage.toLocaleString()}`
                            : '—',
                        },
                        {
                          label: 'National Median Wage',
                          value: s.data.nationalMedianWage
                            ? `$${s.data.nationalMedianWage.toLocaleString()}`
                            : '—',
                        },
                        {
                          label: 'Avg Weekly Wage',
                          value: s.data.avgWeeklyWage
                            ? `$${s.data.avgWeeklyWage.toLocaleString()}`
                            : '—',
                        },
                        {
                          label: 'BEA GDP CAGR',
                          value:
                            s.data.beaGdpCagr != null
                              ? `${(s.data.beaGdpCagr * 100).toFixed(1)}%`
                              : '—',
                        },
                        {
                          label: 'Capital Intensity (FA/GDP)',
                          value: s.data.beaCapitalIntensity?.toFixed(2) ?? '—',
                        },
                        { label: 'Data Year', value: String(s.data.releaseYear ?? '—') },
                      ].map(({ label, value }) => (
                        <div key={label} className="rounded bg-slate-50 p-2">
                          <p className="text-[10px] uppercase tracking-wide text-slate-400">
                            {label}
                          </p>
                          <p className="mt-0.5 font-semibold text-slate-700">{value}</p>
                        </div>
                      ))}
                    </div>

                    {/* Director override form */}
                    <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-4">
                      <OverridePanel
                        naicsCode={s.naicsCode}
                        label={s.label}
                        current={ov}
                        onSave={saveOverride}
                        onDelete={deleteOverride}
                        saving={savingOverride === s.naicsCode}
                      />
                      {ov?.notes && (
                        <p className="mt-2 text-xs text-slate-600">
                          <span className="font-semibold">Note:</span> {ov.notes}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Surface>

      <p className="text-[11px] text-slate-400">
        Sources: BLS OES (wages), BLS QCEW (employment), Census LEHD (commuting), BEA GDP by
        Industry, BEA Fixed Assets. Scores are computed from ingested data and updated when
        pipelines run. Director overrides are saved per organization.
      </p>
    </div>
  );
}
